import "./ViewButton.css";

interface Props {
  onClick: () => void;
  /** Optional — a11y label and tooltip include the doc name for
   *  screen readers ("View invoice-2024.pdf"). Falls back to a
   *  generic label when omitted. */
  documentName?: string;
  disabled?: boolean;
}

/**
 * Icon-only View button — used on every surface that opens the shared
 * DocumentPreview modal (Documents page, Upload Inbox, Dashboard queue).
 *
 * The whole point of extracting this is visual + interaction consistency.
 * Every "View" the user encounters is the same 28×28 outlined eye icon
 * in the info-blue palette, so hovering / clicking / tabbing feels
 * identical across pages. Don't fork the styling per surface — if a
 * caller needs a different affordance it's not this button.
 */
export function ViewButton({ onClick, documentName, disabled }: Props) {
  const label = documentName
    ? `View ${documentName}`
    : "View document";
  return (
    <button
      type="button"
      className="view-btn"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M8 3C4.364 3 1.258 5.434.2 8.8a.5.5 0 0 0 0 .4C1.258 12.566 4.364 15 8 15s6.742-2.434 7.8-5.8a.5.5 0 0 0 0-.4C14.742 5.434 11.636 3 8 3Z"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
        />
        <circle
          cx="8"
          cy="9"
          r="2.5"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
        />
      </svg>
    </button>
  );
}
